import { Button, ButtonLevel, ButtonSize, ScoreStickerCard, ScoreStickerCardSize } from '@vizit/atoms';
import { WeightIndicatorSize } from '@vizit/atoms';
import { ChevronDownIcon, ChevronUpIcon, RefreshCw04Icon, ScoreWeightsIcon } from '@vizit/icons';
import { AssetCountV2, AssetMixV2, AssetOrderV2, Heading, HeadingVariant, ScoreMetricCell } from '@vizit/molecules';
import { ScoreWeightsPanel } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { assetCountCopy, assetMixCopy, assetOrderCopy } from './formula-metric-copy';
import { resolveLevel } from './resolve-level';
import { WEIGHT_METRICS } from './score-weight-constants';
import type { WeightMetricKey } from './score-weight-constants';
import { cardClasses, containerClasses, drawerClasses, drawerContentClasses, metricsClasses } from './styles';
import type { ProductPagePerformanceProps } from './types';

/** The served values every metric contributes to the weights panel. */
type MetricValues = { score?: number; points?: number | null; pointsToGo?: number | null };

/**
 * Product page performance section displaying the main score
 * alongside metric cards for avg. assets score, hero asset score,
 * asset mix, asset count, and asset order.
 *
 * Each metric cell uses the per-category `bolts` from the recommendations
 * response when present, falling back to a hardcoded impact level for
 * legacy/unsynced categories.
 *
 * When `weights` is provided, each cell renders a weight indicator instead of its
 * impact bolt, and a "Score weights" disclosure button reveals a breakdown panel.
 * Hovering a cell while the panel is open isolates that metric (dims the rest and
 * fades the panel's other segments); `highlightedSection` takes precedence over
 * hover isolation, and isolation only applies while the panel is open.
 */
const ProductPagePerformance = React.forwardRef<HTMLDivElement, ProductPagePerformanceProps>((props, forwardedRef) => {
  const {
    pageScore,
    pageScoreDiff,
    avgAssetsScore,
    heroAssetScore,
    assetMix,
    assetCount,
    assetOrder,
    isLoading = false,
    onReorder,
    highlightedSection,
    weights,
    defaultWeightsOpen = false,
    formulaTotal,
    className,
    ...other
  } = props;

  const [isWeightsOpen, setIsWeightsOpen] = React.useState(defaultWeightsOpen);
  const [hoveredMetric, setHoveredMetric] = React.useState<WeightMetricKey | null>(null);
  const panelId = React.useId();

  const hasHighlight = highlightedSection != null;

  const hasWeights = weights != null;
  // The weighted Asset Order cell shows a Reorder button under its notes while assets are out of order;
  // every cell then reserves that space so all the notes line up on one row.
  const showReorder = hasWeights && assetOrder.score != null && onReorder != null && !assetOrder.isSorted;

  // A background refetch can return a payload that fails the formula gate mid-session,
  // dropping `weights` to null while the panel is open or a cell is hovered. Reset both
  // pieces of stored state right here — React's "adjust state during render" pattern —
  // instead of in an effect, so neither a stale `isWeightsOpen` nor a stale
  // `hoveredMetric` can outlive the weights and reapply themselves (silent panel reopen,
  // phantom isolation) if a later refetch brings the weights back.
  if (!hasWeights && isWeightsOpen) {
    setIsWeightsOpen(false);
  }
  if (!hasWeights && hoveredMetric != null) {
    setHoveredMetric(null);
  }

  const isPanelOpen = hasWeights && isWeightsOpen;
  // Step-card highlighting (highlightedSection) takes precedence over hover isolation,
  // and drives the SAME isolation as a cell hover — hovering "Improve hero image score"
  // focuses the hero cell, bar segments, insight line and formula term together.
  // Isolation only exists while the panel is open.
  const highlightedMetric: WeightMetricKey | null =
    highlightedSection === 'carousel-scores'
      ? 'carousel'
      : highlightedSection === 'hero-score'
        ? 'hero'
        : highlightedSection === 'asset-mix'
          ? 'assetMix'
          : null;
  const isolatedMetric = isPanelOpen ? (hasHighlight ? highlightedMetric : hoveredMetric) : null;

  // One rule for every cell: a metric is focused when its step card is highlighted or it
  // is the isolated one, and disabled whenever another metric holds either of those.
  const isCellFocused = (key: WeightMetricKey) => highlightedMetric === key || isolatedMetric === key;
  const isCellDisabled = (key: WeightMetricKey) =>
    (hasHighlight && highlightedMetric !== key) || (isolatedMetric != null && isolatedMetric !== key);

  const weightMetric = (key: WeightMetricKey) => WEIGHT_METRICS.find((m) => m.key === key);

  const cellWeight = (key: WeightMetricKey) =>
    hasWeights
      ? {
          pct: weights[key],
          accentClassName: isPanelOpen ? weightMetric(key)?.accentClassName : undefined,
          size: isPanelOpen ? WeightIndicatorSize.Lg : WeightIndicatorSize.Md,
        }
      : undefined;

  // The served per-metric values the panel's formula and split bars read, keyed like the weights.
  const metrics: Record<WeightMetricKey, MetricValues> = {
    carousel: avgAssetsScore,
    hero: heroAssetScore,
    assetMix,
    assetCount,
    assetOrder,
  };

  const hoverHandlers = (key: WeightMetricKey) =>
    hasWeights
      ? {
          onMouseEnter: () => setHoveredMetric(key),
          onMouseLeave: () => setHoveredMetric((current) => (current === key ? null : current)),
          onFocus: () => setHoveredMetric(key),
          onBlur: () => setHoveredMetric((current) => (current === key ? null : current)),
        }
      : {};

  const isScoreMuted = (key: WeightMetricKey) => isPanelOpen && isolatedMetric !== key;
  // The main score stands prominent until the breakdown opens, then tucks in level with the cells.
  const isScoreProminent = !isPanelOpen;

  const cellClassName = (key: WeightMetricKey) =>
    isolatedMetric === key ? cx('flex-1', 'border-b-2', weightMetric(key)?.underlineClassName) : 'flex-1';

  const mixCopy = assetMixCopy(assetMix);
  const countCopy = assetCountCopy(assetCount);
  const orderCopy = assetOrderCopy(assetOrder);

  return (
    <div
      ref={forwardedRef}
      className={cx(containerClasses(), className)}
      data-testid="product-page-performance"
      {...other}
    >
      <div className="flex items-center justify-between">
        <Heading variant={HeadingVariant.H6}>Product page performance</Heading>
        {hasWeights && !isLoading && (
          <Button
            level={ButtonLevel.Ghost}
            size={ButtonSize.Sm}
            aria-expanded={isPanelOpen}
            aria-controls={panelId}
            onPress={() => {
              // Closing no longer unmounts the panel, so its hover cleanup doesn't fire; a pointer resting on a
              // segment would otherwise leave that metric isolated when the panel reopens.
              if (isPanelOpen) setHoveredMetric(null);
              setIsWeightsOpen(!isPanelOpen);
            }}
          >
            <ScoreWeightsIcon size={16} />
            Score weights
            {isPanelOpen ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
          </Button>
        )}
      </div>

      <div className="flex flex-col">
        <div className={cardClasses({ isProminent: isScoreProminent })}>
          <ScoreStickerCard
            label="PRODUCT PAGE SCORE"
            score={pageScore}
            scoreDiff={pageScoreDiff}
            size={isScoreProminent ? ScoreStickerCardSize.Lg : ScoreStickerCardSize.Md}
            isLoading={isLoading}
            isMuted={!isScoreProminent}
            isDisabled={hasHighlight || isolatedMetric != null}
            data-testid="product-page-score"
          />

          <div className={metricsClasses()}>
            <ScoreMetricCell
              reserveActionSpace={showReorder}
              label={avgAssetsScore.label}
              score={avgAssetsScore.score}
              impactLevel={resolveLevel(avgAssetsScore.bolts, 'high')}
              weight={cellWeight('carousel')}
              scoreDiff={avgAssetsScore.scoreDiff}
              isLoading={isLoading}
              isFocused={isCellFocused('carousel')}
              isDisabled={isCellDisabled('carousel')}
              isScoreMuted={isScoreMuted('carousel')}
              className={cellClassName('carousel')}
              {...hoverHandlers('carousel')}
            />
            <ScoreMetricCell
              reserveActionSpace={showReorder}
              label={heroAssetScore.label}
              score={heroAssetScore.score}
              impactLevel={resolveLevel(heroAssetScore.bolts, 'medium')}
              weight={cellWeight('hero')}
              scoreDiff={heroAssetScore.scoreDiff}
              isLoading={isLoading}
              isFocused={isCellFocused('hero')}
              isDisabled={isCellDisabled('hero')}
              isScoreMuted={isScoreMuted('hero')}
              className={cellClassName('hero')}
              {...hoverHandlers('hero')}
            />
            {hasWeights && assetMix.score != null ? (
              <ScoreMetricCell
                reserveActionSpace={showReorder}
                label="ASSET MIX"
                score={assetMix.score}
                impactLevel={resolveLevel(assetMix.bolts, 'medium')}
                weight={cellWeight('assetMix')}
                notes={mixCopy.notes}
                infoTooltip={mixCopy.tooltip}
                isLoading={isLoading}
                isFocused={isCellFocused('assetMix')}
                isDisabled={isCellDisabled('assetMix')}
                isScoreMuted={isScoreMuted('assetMix')}
                className={cellClassName('assetMix')}
                {...hoverHandlers('assetMix')}
              />
            ) : (
              <AssetMixV2
                missing={assetMix.missing}
                required={assetMix.required}
                impactLevel={resolveLevel(assetMix.bolts, 'medium')}
                isLoading={isLoading}
                isFocused={isCellFocused('assetMix')}
                isDisabled={isCellDisabled('assetMix')}
                className="flex-1"
                {...hoverHandlers('assetMix')}
              />
            )}
            {hasWeights && assetCount.score != null ? (
              <ScoreMetricCell
                reserveActionSpace={showReorder}
                label="ASSET COUNT"
                score={assetCount.score}
                impactLevel={resolveLevel(assetCount.bolts, 'low')}
                weight={cellWeight('assetCount')}
                notes={countCopy.notes}
                infoTooltip={countCopy.tooltip}
                isLoading={isLoading}
                isFocused={isCellFocused('assetCount')}
                isDisabled={isCellDisabled('assetCount')}
                isScoreMuted={isScoreMuted('assetCount')}
                className={cellClassName('assetCount')}
                {...hoverHandlers('assetCount')}
              />
            ) : (
              <AssetCountV2
                count={assetCount.count}
                optimalMin={assetCount.optimalMin}
                optimalMax={assetCount.optimalMax}
                impactLevel={resolveLevel(assetCount.bolts, 'low')}
                isLoading={isLoading}
                isFocused={isCellFocused('assetCount')}
                isDisabled={isCellDisabled('assetCount')}
                className="flex-1"
                {...hoverHandlers('assetCount')}
              />
            )}
            {hasWeights && assetOrder.score != null ? (
              <ScoreMetricCell
                reserveActionSpace={showReorder}
                label="ASSET ORDER"
                score={assetOrder.score}
                impactLevel={resolveLevel(assetOrder.bolts, 'very-low')}
                weight={cellWeight('assetOrder')}
                notes={orderCopy.notes}
                infoTooltip={orderCopy.tooltip}
                action={
                  showReorder ? (
                    <Button
                      level={ButtonLevel.Secondary}
                      size={ButtonSize.Xs}
                      iconStart={<RefreshCw04Icon size={14} />}
                      isDisabled={isCellDisabled('assetOrder')}
                      onPress={onReorder}
                    >
                      Reorder
                    </Button>
                  ) : undefined
                }
                isLoading={isLoading}
                isFocused={isCellFocused('assetOrder')}
                isDisabled={isCellDisabled('assetOrder')}
                isScoreMuted={isScoreMuted('assetOrder')}
                className={cellClassName('assetOrder')}
                {...hoverHandlers('assetOrder')}
              />
            ) : (
              <AssetOrderV2
                isSorted={assetOrder.isSorted}
                impactLevel={resolveLevel(assetOrder.bolts, 'very-low')}
                isLoading={isLoading}
                isFocused={isCellFocused('assetOrder')}
                isDisabled={isCellDisabled('assetOrder')}
                onReorder={onReorder}
                className="flex-1"
                {...hoverHandlers('assetOrder')}
              />
            )}
          </div>
        </div>

        {/* Stays mounted while closed so the panel can slide open and shut: the row grows from 0fr to
            1fr, pushing the content below it down, and the closed panel is inert. */}
        {hasWeights && !isLoading && (
          <div className={drawerClasses({ isOpen: isPanelOpen })} inert={!isPanelOpen}>
            <div className={drawerContentClasses({ isOpen: isPanelOpen })}>
              <div className="pt-2">
                <ScoreWeightsPanel
                  id={panelId}
                  onMetricHover={(key) => setHoveredMetric(key as WeightMetricKey | null)}
                  items={WEIGHT_METRICS.map((m) => ({
                    key: m.key,
                    label: m.label,
                    weightPct: weights[m.key],
                    segmentClassName: m.segmentClassName,
                    barClassName: m.barClassName,
                    availableClassName: m.availableClassName,
                    availableTextClassName: m.availableTextClassName,
                    accentClassName: m.accentClassName,
                    insightLabel: m.insightLabel,
                    score: metrics[m.key].score,
                    points: metrics[m.key].points,
                    pointsToGo: metrics[m.key].pointsToGo,
                  }))}
                  isolatedKey={isolatedMetric}
                  total={formulaTotal ?? null}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

ProductPagePerformance.displayName = 'ProductPagePerformance';

export { ProductPagePerformance };
export type { ProductPagePerformanceProps };
